// src/Components/Admin/Settings.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';

// --- NEW NAVIGATION & SECURITY ICONS ---
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

// --- DIALOG IMPORTS ---
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

// Firestore & Storage Imports
import { doc, getDoc, setDoc, deleteField } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../Firebase/firebase';

// Import Custom Components
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import SettingsRestricted from './SettingsRestricted';
import SettingsMainLogo from './SettingsMainLogo';
import SettingsAIPrompts from './SettingsAIPrompts';
import SettingsOther from './SettingsOther';
import SettingsSocialMedia from './SettingsSocialMedia';

// Import Custom Icons
import {
  MenuAddNewGrey,
  MenuSave,
  MenuSaveGrey,
  MenuDeleteGrey,
  MenuIconCancel,
  MenuIconCancelGrey,
} from '../../assets/icons/CustomIcons';

import AdminBg from '../../assets/images/AdminBackground.png';

// --- STYLING CONSTANTS ---
const ICON_SIZE = 64;
const STEPS = ['PAGE1', 'PAGE2', 'PAGE3', 'PAGE4', 'PAGE5'];

const DEFAULT_AI_PROMPT = `Write 3 distinct variations of a professional, two-sentence description for the following location: "{{query}}". Provide only factual descriptions without any promotional language, introductory text, or markdown formatting. Return the response as a JSON array of exactly 3 strings.`;

const DEFAULT_SOCIAL_LINKS = [
  { platform: 'Facebook', url: '' },
  { platform: 'Instagram', url: '' },
  { platform: 'LinkedIn', url: '' },
  { platform: 'YouTube', url: '' },
  { platform: 'X', url: '' },
];

// Reusable cancel button styles
const cancelButtonSx = {
  textTransform: 'none',
  minWidth: 120,
  borderWidth: 2,
  fontWeight: 500,
  '&:hover': {
    borderWidth: 2,
    backgroundColor: 'rgba(255,0,0,0.08)',
  },
  '&.Mui-disabled': {
    borderColor: '#d32f2f',
    color: '#d32f2f',
    opacity: 0.5,
  },
};

export default function Settings({ setIsDirty }) {
  // --- STATE ---
  // Pagination State
  const [viewMode, setViewMode] = useState('PAGE1');

  // Data State
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [originalUrl, setOriginalUrl] = useState('');

  // Email From State
  const [emailFrom, setEmailFrom] = useState('');
  const [originalEmailFrom, setOriginalEmailFrom] = useState('');

  // Logo State
  const [logoUrl, setLogoUrl] = useState('');
  const [originalLogoUrl, setOriginalLogoUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null);

  // Logo Scale State (Numeric %)
  const [logoScale, setLogoScale] = useState(100);
  const [originalScale, setOriginalScale] = useState(100);

  // Icon Scale State (Numeric %)
  const [iconScale, setIconScale] = useState(100);
  const [originalIconScale, setOriginalIconScale] = useState(100);

  // Default Make Public State (Days)
  const [makePublicDays, setMakePublicDays] = useState('0');
  const [originalMakePublicDays, setOriginalMakePublicDays] = useState('0');

  // RTE Default Text State
  const [rteFontType, setRteFontType] = useState('Helvetica');
  const [originalRteFontType, setOriginalRteFontType] = useState('Helvetica');
  const [rteFontSize, setRteFontSize] = useState('10pt');
  const [originalRteFontSize, setOriginalRteFontSize] = useState('10pt');

  // IM Default Heading State
  const [imHeadingFontType, setImHeadingFontType] = useState('Helvetica');
  const [originalImHeadingFontType, setOriginalImHeadingFontType] = useState('Helvetica');
  const [imHeadingFontSize, setImHeadingFontSize] = useState('12pt');
  const [originalImHeadingFontSize, setOriginalImHeadingFontSize] = useState('12pt');

  // Promo Docs Row Break State
  const [allowRowBreaks, setAllowRowBreaks] = useState(true);
  const [originalAllowRowBreaks, setOriginalAllowRowBreaks] = useState(true);

  // Promo Images Rounded Corners State
  const [useRoundedCorners, setUseRoundedCorners] = useState(false);
  const [originalUseRoundedCorners, setOriginalUseRoundedCorners] = useState(false);
  const [roundedCornerRadius, setRoundedCornerRadius] = useState(10);
  const [originalRoundedCornerRadius, setOriginalRoundedCornerRadius] = useState(10);

  // Text Formatting State
  const [makeUppercase, setMakeUppercase] = useState(true);
  const [originalMakeUppercase, setOriginalMakeUppercase] = useState(true);

  // Broadcast Delay & Security State
  const [broadcastDelay, setBroadcastDelay] = useState('48:00:00');
  const [originalBroadcastDelay, setOriginalBroadcastDelay] = useState('48:00:00');

  // Unsubscribed/Bounces Delete Lock State
  const [deleteBouncesLock, setDeleteBouncesLock] = useState(true);
  const [originalDeleteBouncesLock, setOriginalDeleteBouncesLock] = useState(true);

  // Import Limit State
  const [importLimit, setImportLimit] = useState(100);
  const [originalImportLimit, setOriginalImportLimit] = useState(100);

  // AI Prompt State
  const [descriptionPrompt, setDescriptionPrompt] = useState(DEFAULT_AI_PROMPT);
  const [originalDescriptionPrompt, setOriginalDescriptionPrompt] =
    useState(DEFAULT_AI_PROMPT);

  // Social Media State
  const [socialLinks, setSocialLinks] = useState(() =>
    DEFAULT_SOCIAL_LINKS.map((l) => ({ ...l })),
  );
  const [originalSocialLinks, setOriginalSocialLinks] = useState(() =>
    DEFAULT_SOCIAL_LINKS.map((l) => ({ ...l })),
  );

  // Consolidated Security Lock
  const [isSecurityGroupLocked, setIsSecurityGroupLocked] = useState(true);

  const [actualPassword, setActualPassword] = useState('admin'); // Default secure fallback

  // Password Dialog State
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [confirmPasswordInput, setConfirmPasswordInput] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [isDialogProcessing, setIsDialogProcessing] = useState(false);
  const passwordInputRef = useRef(null); // Ref for auto-focusing the password field

  // Visibility Eye Toggle States
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Restricted Settings Reference State
  const restrictedSettingsRef = useRef(null);
  const [isRestrictedDirty, setIsRestrictedDirty] = useState(false);

  // Alert State
  const [showCloseAlert, setShowCloseAlert] = useState(false);

  // Error/Loading State
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorSeverity, setErrorSeverity] = useState('error');
  const [isProcessing, setIsProcessing] = useState(false);

  // File Input Ref
  const fileInputRef = useRef(null);

  const toolBlockStyle = {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minWidth: 0,
  };

  const isSocialLinksDirty =
    JSON.stringify(socialLinks) !== JSON.stringify(originalSocialLinks);

  const isFormDirty =
    websiteUrl !== originalUrl ||
    emailFrom !== originalEmailFrom ||
    logoUrl !== originalLogoUrl ||
    logoScale !== originalScale ||
    iconScale !== originalIconScale ||
    String(makePublicDays) !== String(originalMakePublicDays) ||
    rteFontType !== originalRteFontType ||
    rteFontSize !== originalRteFontSize ||
    imHeadingFontType !== originalImHeadingFontType ||
    imHeadingFontSize !== originalImHeadingFontSize ||
    allowRowBreaks !== originalAllowRowBreaks ||
    useRoundedCorners !== originalUseRoundedCorners ||
    String(roundedCornerRadius) !== String(originalRoundedCornerRadius) ||
    makeUppercase !== originalMakeUppercase ||
    broadcastDelay !== originalBroadcastDelay ||
    !!deleteBouncesLock !== !!originalDeleteBouncesLock ||
    importLimit !== originalImportLimit ||
    descriptionPrompt !== originalDescriptionPrompt ||
    isSocialLinksDirty ||
    isRestrictedDirty;

  useEffect(() => {
    if (setIsDirty) {
      setIsDirty(isFormDirty);
    }
  }, [isFormDirty, setIsDirty]);

  // --- FOCUS MANAGEMENT ---
  useEffect(() => {
    if (showPasswordDialog) {
      const timer = setTimeout(() => {
        if (passwordInputRef.current) {
          passwordInputRef.current.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [showPasswordDialog, isChangingPassword]);

  // --- FIRESTORE FUNCTIONS ---
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // 1. Fetch Website
        const webRef = doc(db, 'settings', 'MainWebSite');
        const webSnap = await getDoc(webRef);
        if (webSnap.exists()) {
          const data = webSnap.data();
          if (data.url) {
            setWebsiteUrl(data.url);
            setOriginalUrl(data.url);
          }
        }

        // 1b. Fetch Email From
        const emailRef = doc(db, 'settings', 'EmailFrom');
        const emailSnap = await getDoc(emailRef);
        if (emailSnap.exists()) {
          const data = emailSnap.data();
          if (data.emailFrom) {
            setEmailFrom(data.emailFrom);
            setOriginalEmailFrom(data.emailFrom);
          }
        }

        // 2. Fetch Logo
        const logoRef = doc(db, 'settings', 'MainLogo');
        const logoSnap = await getDoc(logoRef);
        if (logoSnap.exists()) {
          const data = logoSnap.data();
          if (data.url) {
            setLogoUrl(data.url);
            setOriginalLogoUrl(data.url);
          }
          const scalePref = typeof data.scale === 'number' ? data.scale : 100;
          setLogoScale(scalePref);
          setOriginalScale(scalePref);
        } else {
          setLogoScale(100);
          setOriginalScale(100);
        }

        // 3. Fetch Icon Scale
        const iconRef = doc(db, 'settings', 'IconScale');
        const iconSnap = await getDoc(iconRef);
        if (iconSnap.exists()) {
          const data = iconSnap.data();
          const scalePref = typeof data.scale === 'number' ? data.scale : 100;
          setIconScale(scalePref);
          setOriginalIconScale(scalePref);
        } else {
          setIconScale(100);
          setOriginalIconScale(100);
        }

        // 4. Fetch Make Public Default
        const publicRef = doc(db, 'settings', 'MakePublic');
        const publicSnap = await getDoc(publicRef);
        if (publicSnap.exists()) {
          const data = publicSnap.data();
          const days =
            typeof data.makePublicDefault === 'number' ? data.makePublicDefault : 0;
          setMakePublicDays(String(days));
          setOriginalMakePublicDays(String(days));
        } else {
          setMakePublicDays('0');
          setOriginalMakePublicDays('0');
        }

        // 4b. Fetch RTE Default Text Settings
        const rteRef = doc(db, 'settings', 'RTEDefaultText');
        const rteSnap = await getDoc(rteRef);
        if (rteSnap.exists()) {
          const data = rteSnap.data();
          if (data.FontType) {
            setRteFontType(data.FontType);
            setOriginalRteFontType(data.FontType);
          }
          if (data.FontSize) {
            setRteFontSize(data.FontSize);
            setOriginalRteFontSize(data.FontSize);
          }
        }

        // 4c. Fetch Import Markers Default Heading
        const headingRef = doc(db, 'settings', 'RTEDefaultHeading');
        const headingSnap = await getDoc(headingRef);
        if (headingSnap.exists()) {
          const data = headingSnap.data();
          if (data.FontType) {
            setImHeadingFontType(data.FontType);
            setOriginalImHeadingFontType(data.FontType);
          }
          if (data.FontSize) {
            setImHeadingFontSize(data.FontSize);
            setOriginalImHeadingFontSize(data.FontSize);
          }
        }

        // 5. Fetch Promo Docs Settings
        const promoRef = doc(db, 'settings', 'PromoSettings');
        const promoSnap = await getDoc(promoRef);
        if (promoSnap.exists()) {
          const data = promoSnap.data();
          const allowBreaks =
            data.allowRowBreaks !== undefined ? !!data.allowRowBreaks : true;
          const useRounded = !!data.useRoundedCorners;
          const rcRadius =
            data.roundedCornerRadius !== undefined ? data.roundedCornerRadius : 10;

          setAllowRowBreaks(allowBreaks);
          setOriginalAllowRowBreaks(allowBreaks);
          setUseRoundedCorners(useRounded);
          setOriginalUseRoundedCorners(useRounded);
          setRoundedCornerRadius(rcRadius);
          setOriginalRoundedCornerRadius(rcRadius);
        } else {
          setAllowRowBreaks(true);
          setOriginalAllowRowBreaks(true);
          setUseRoundedCorners(false);
          setOriginalUseRoundedCorners(false);
          setRoundedCornerRadius(10);
          setOriginalRoundedCornerRadius(10);
        }

        // 6. Fetch Text Formatting Settings
        const textFmtRef = doc(db, 'settings', 'TextFormatting');
        const textFmtSnap = await getDoc(textFmtRef);
        if (textFmtSnap.exists()) {
          const data = textFmtSnap.data();
          const isUppercase =
            data.makeUppercase !== undefined ? !!data.makeUppercase : true;
          setMakeUppercase(isUppercase);
          setOriginalMakeUppercase(isUppercase);
        } else {
          setMakeUppercase(true);
          setOriginalMakeUppercase(true);
        }

        // 7. Fetch Broadcast Delay Settings
        const delayRef = doc(db, 'settings', 'BroadcastDelay');
        const delaySnap = await getDoc(delayRef);
        if (delaySnap.exists()) {
          const data = delaySnap.data();
          if (data.delay) {
            setBroadcastDelay(data.delay);
            setOriginalBroadcastDelay(data.delay);
          }
        }

        // 8. Fetch UnsubscribedBouncesLock Settings
        const lockRef = doc(db, 'settings', 'UnsubscribedBouncesLock');
        const lockSnap = await getDoc(lockRef);
        if (lockSnap.exists()) {
          const data = lockSnap.data();
          if (data.locksetting !== undefined) {
            setDeleteBouncesLock(!!data.locksetting);
            setOriginalDeleteBouncesLock(!!data.locksetting);
          } else {
            setDeleteBouncesLock(true);
            setOriginalDeleteBouncesLock(true);
          }
        } else {
          setDeleteBouncesLock(true);
          setOriginalDeleteBouncesLock(true);
        }

        // 9. Fetch Import Limit Settings
        const limitRef = doc(db, 'settings', 'ImportLimit');
        const limitSnap = await getDoc(limitRef);
        if (limitSnap.exists()) {
          const data = limitSnap.data();
          if (data.limit !== undefined) {
            setImportLimit(Number(data.limit));
            setOriginalImportLimit(Number(data.limit));
          } else {
            setImportLimit(100);
            setOriginalImportLimit(100);
          }
        } else {
          setImportLimit(100);
          setOriginalImportLimit(100);
        }

        // 10. Fetch Restricted Password (Consolidated)
        const pwdRef = doc(db, 'settings', 'RestrictedPassword');
        const pwdSnap = await getDoc(pwdRef);
        if (pwdSnap.exists()) {
          const data = pwdSnap.data();
          if (data.password) {
            setActualPassword(data.password);
          }
        }

        // 11. Fetch AI Prompts
        const aiPromptsRef = doc(db, 'settings', 'AIPrompts');
        const aiPromptsSnap = await getDoc(aiPromptsRef);
        if (aiPromptsSnap.exists()) {
          const data = aiPromptsSnap.data();
          if (data.locationDescription) {
            setDescriptionPrompt(data.locationDescription);
            setOriginalDescriptionPrompt(data.locationDescription);
          }
        }

        // 12. Fetch Social Media Links
        const socialRef = doc(db, 'settings', 'SocialMediaLinks');
        const socialSnap = await getDoc(socialRef);
        if (socialSnap.exists()) {
          const data = socialSnap.data();
          if (data.links && Array.isArray(data.links)) {
            const merged = DEFAULT_SOCIAL_LINKS.map((def) => {
              const found = data.links.find((l) => l.platform === def.platform);
              return found ? { ...found } : { ...def };
            });
            setSocialLinks(merged);
            setOriginalSocialLinks(merged.map((l) => ({ ...l })));
          }
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const saveToFirestore = async () => {
    setIsProcessing(true);
    try {
      let finalLogoUrl = logoUrl;

      // 1. Delete old image if changed
      if (originalLogoUrl && (logoUrl === '' || logoFile)) {
        try {
          const oldImageRef = ref(storage, originalLogoUrl);
          await deleteObject(oldImageRef);
        } catch (deleteError) {
          console.warn('Could not delete old image:', deleteError);
        }
      }

      // 2. Upload new image
      if (logoFile) {
        try {
          const storageRef = ref(storage, 'clientLogo/logo.png');
          const snapshot = await uploadBytes(storageRef, logoFile);
          finalLogoUrl = await getDownloadURL(snapshot.ref);
        } catch (storageError) {
          throw new Error(`Storage Upload Failed: ${storageError.message}`);
        }
      }

      // 3. Trigger Sub-Component Saves (Restricted Settings)
      if (restrictedSettingsRef.current && restrictedSettingsRef.current.isDirty()) {
        await restrictedSettingsRef.current.save();
      }

      // 4. Save to Firestore

      // Website
      const webRef = doc(db, 'settings', 'MainWebSite');
      await setDoc(webRef, { url: websiteUrl }, { merge: true });

      // Email From
      const emailRef = doc(db, 'settings', 'EmailFrom');
      await setDoc(emailRef, { emailFrom }, { merge: true });

      // Logo
      const logoRef = doc(db, 'settings', 'MainLogo');
      await setDoc(logoRef, { url: finalLogoUrl, scale: logoScale }, { merge: true });

      // Icon Scale
      const iconRef = doc(db, 'settings', 'IconScale');
      await setDoc(iconRef, { scale: iconScale }, { merge: true });

      // Make Public Default
      const daysToSave = makePublicDays === '' ? 0 : parseInt(makePublicDays, 10);
      const publicRef = doc(db, 'settings', 'MakePublic');
      await setDoc(publicRef, { makePublicDefault: daysToSave }, { merge: true });

      // RTE Default Text
      const rteRef = doc(db, 'settings', 'RTEDefaultText');
      await setDoc(
        rteRef,
        {
          FontType: rteFontType,
          FontSize: rteFontSize,
        },
        { merge: true },
      );

      // Import Markers Default Heading
      const headingRef = doc(db, 'settings', 'RTEDefaultHeading');
      await setDoc(
        headingRef,
        {
          FontType: imHeadingFontType,
          FontSize: imHeadingFontSize,
        },
        { merge: true },
      );

      // Promo Settings
      const promoRef = doc(db, 'settings', 'PromoSettings');
      await setDoc(
        promoRef,
        {
          allowRowBreaks,
          useRoundedCorners,
          roundedCornerRadius: Number(roundedCornerRadius),
        },
        { merge: true },
      );

      // Text Formatting
      const textFmtRef = doc(db, 'settings', 'TextFormatting');
      await setDoc(textFmtRef, { makeUppercase }, { merge: true });

      // AI Prompts
      const aiPromptsRef = doc(db, 'settings', 'AIPrompts');
      await setDoc(
        aiPromptsRef,
        { locationDescription: descriptionPrompt },
        { merge: true },
      );

      // Social Media Links
      const socialRef = doc(db, 'settings', 'SocialMediaLinks');
      await setDoc(socialRef, { links: socialLinks }, { merge: true });

      // Security Settings (Update values and explicitly delete legacy password fields)
      const delayRef = doc(db, 'settings', 'BroadcastDelay');
      await setDoc(
        delayRef,
        { delay: broadcastDelay, password: deleteField() },
        { merge: true },
      );

      const lockRef = doc(db, 'settings', 'UnsubscribedBouncesLock');
      await setDoc(
        lockRef,
        { locksetting: !!deleteBouncesLock, password: deleteField() },
        { merge: true },
      );

      const limitRef = doc(db, 'settings', 'ImportLimit');
      await setDoc(
        limitRef,
        { limit: importLimit, password: deleteField() },
        { merge: true },
      );

      // Update Local State
      setOriginalUrl(websiteUrl);
      setOriginalEmailFrom(emailFrom);
      setOriginalLogoUrl(finalLogoUrl);
      setLogoUrl(finalLogoUrl);
      setLogoFile(null);
      setOriginalScale(logoScale);
      setOriginalIconScale(iconScale);
      setOriginalMakePublicDays(String(daysToSave));
      setMakePublicDays(String(daysToSave));
      setOriginalRteFontType(rteFontType);
      setOriginalRteFontSize(rteFontSize);
      setOriginalImHeadingFontType(imHeadingFontType);
      setOriginalImHeadingFontSize(imHeadingFontSize);
      setOriginalAllowRowBreaks(allowRowBreaks);
      setOriginalUseRoundedCorners(useRoundedCorners);
      setOriginalRoundedCornerRadius(roundedCornerRadius);
      setOriginalMakeUppercase(makeUppercase);
      setOriginalBroadcastDelay(broadcastDelay);
      setOriginalDeleteBouncesLock(!!deleteBouncesLock);
      setOriginalImportLimit(importLimit);
      setOriginalDescriptionPrompt(descriptionPrompt);
      setOriginalSocialLinks(socialLinks.map((l) => ({ ...l })));

      console.log('Successfully saved Settings');
    } catch (error) {
      console.error('Error saving to Firestore:', error);
      setErrorSeverity('error');
      setErrorMessage(error.message || 'An error occurred while saving.');
      setShowErrorDialog(true);
    } finally {
      setIsProcessing(false);
    }
  };

  // --- ACTIONS ---
  const revertChanges = useCallback(() => {
    setWebsiteUrl(originalUrl);
    setEmailFrom(originalEmailFrom);
    setLogoUrl(originalLogoUrl);
    setLogoFile(null);
    setLogoScale(originalScale);
    setIconScale(originalIconScale);
    setMakePublicDays(originalMakePublicDays);
    setRteFontType(originalRteFontType);
    setRteFontSize(originalRteFontSize);
    setImHeadingFontType(originalImHeadingFontType);
    setImHeadingFontSize(originalImHeadingFontSize);
    setAllowRowBreaks(originalAllowRowBreaks);
    setUseRoundedCorners(originalUseRoundedCorners);
    setRoundedCornerRadius(originalRoundedCornerRadius);
    setMakeUppercase(originalMakeUppercase);
    setBroadcastDelay(originalBroadcastDelay);
    setDeleteBouncesLock(originalDeleteBouncesLock);
    setImportLimit(originalImportLimit);
    setDescriptionPrompt(originalDescriptionPrompt);
    setSocialLinks(originalSocialLinks.map((l) => ({ ...l })));
    setIsSecurityGroupLocked(true);

    if (restrictedSettingsRef.current) {
      restrictedSettingsRef.current.revert();
    }
  }, [
    originalUrl,
    originalEmailFrom,
    originalLogoUrl,
    originalScale,
    originalIconScale,
    originalMakePublicDays,
    originalRteFontType,
    originalRteFontSize,
    originalImHeadingFontType,
    originalImHeadingFontSize,
    originalAllowRowBreaks,
    originalUseRoundedCorners,
    originalRoundedCornerRadius,
    originalMakeUppercase,
    originalBroadcastDelay,
    originalDeleteBouncesLock,
    originalImportLimit,
    originalDescriptionPrompt,
    originalSocialLinks,
  ]);

  const executeClose = useCallback(() => {
    revertChanges();
    setShowCloseAlert(false);
    console.log('Settings Panel Closed');
  }, [revertChanges]);

  const handleHeaderAction = async (action) => {
    if (action === 'CLOSE') {
      if (isFormDirty) setShowCloseAlert(true);
      else executeClose();
    } else if (action === 'SAVE') {
      await saveToFirestore();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      // If a dialog is open, let the dialog handle its own ESC key events natively
      if (showCloseAlert || showPasswordDialog || showErrorDialog) {
        return;
      }

      if (
        e.key === 'Escape' &&
        document.activeElement &&
        document.activeElement.tagName === 'INPUT'
      ) {
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (isFormDirty) {
          setShowCloseAlert(true);
        } else {
          executeClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [showCloseAlert, showPasswordDialog, showErrorDialog, isFormDirty, executeClose]);

  // --- PASSWORD & FORMATTING UTILS ---
  const formatDelayString = (val) => {
    if (!val) return '00:00:00';
    const parts = val.split(':');
    const h = (parts[0] || '0').trim().padStart(2, '0');
    const m = (parts[1] || '0').trim().padStart(2, '0');
    const s = (parts[2] || '0').trim().padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const hashString = async (string) => {
    const utf8 = new TextEncoder().encode(string);
    const hashBuffer = await crypto.subtle.digest('SHA-256', utf8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((bytes) => bytes.toString(16).padStart(2, '0')).join('');
  };

  const openPasswordDialog = () => {
    setDialogError('');
    setPasswordInput('');
    setNewPasswordInput('');
    setConfirmPasswordInput('');
    setIsChangingPassword(false);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setShowPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setShowPasswordDialog(false);
  };

  const handleUnlockSubmit = async () => {
    setIsDialogProcessing(true);
    try {
      // Intentional UI delay to prevent rapid flicker and show loading state
      await new Promise((resolve) => setTimeout(resolve, 600));

      const hashedInput = await hashString(passwordInput);
      if (passwordInput === actualPassword || hashedInput === actualPassword) {
        setIsSecurityGroupLocked(false);
        handleClosePasswordDialog();
      } else {
        setDialogError('Incorrect password');
      }
    } finally {
      setIsDialogProcessing(false);
    }
  };

  const handleChangePasswordSubmit = async () => {
    const hashedCurrentInput = await hashString(passwordInput);

    if (passwordInput !== actualPassword && hashedCurrentInput !== actualPassword) {
      setDialogError('Incorrect current password');
      return;
    }
    if (newPasswordInput !== confirmPasswordInput) {
      setDialogError('New passwords do not match');
      return;
    }
    if (newPasswordInput.length < 4) {
      setDialogError('Password too short (min 4 characters)');
      return;
    }

    setIsDialogProcessing(true);

    try {
      // Intentional UI delay to prevent rapid flicker and show loading state
      await new Promise((resolve) => setTimeout(resolve, 600));

      const hashedNewPassword = await hashString(newPasswordInput);

      // Save to single consolidated RestrictedPassword doc
      const pwdRef = doc(db, 'settings', 'RestrictedPassword');
      await setDoc(pwdRef, { password: hashedNewPassword }, { merge: true });

      setActualPassword(hashedNewPassword);
      setDialogError('');
      setIsChangingPassword(false);
      setShowPasswordDialog(false);

      setErrorSeverity('success');
      setErrorMessage('Security lock password successfully updated.');
      setShowErrorDialog(true);
    } catch (e) {
      console.error('Error saving new password:', e);
      setDialogError('Database error. Could not save password.');
    } finally {
      setIsDialogProcessing(false);
    }
  };

  // --- HANDLERS ---
  const handleTextChange = (e) => setWebsiteUrl(e.target.value);
  const handleImportClick = () => fileInputRef.current && fileInputRef.current.click();
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoUrl(URL.createObjectURL(file));
      setLogoScale(100);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleEraserClick = () => {
    setLogoUrl('');
    setLogoFile(null);
    setLogoScale(100);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // --- SCALE HANDLERS ---
  const handleScaleChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val)) {
      setLogoScale(val);
    }
  };
  const incrementScale = () => setLogoScale((prev) => prev + 5);
  const decrementScale = () => setLogoScale((prev) => Math.max(5, prev - 5));

  // --- ICON SCALE HANDLERS ---
  const handleIconScaleChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val)) {
      setIconScale(val);
    }
  };
  const incrementIconScale = () => setIconScale((prev) => prev + 5);
  const decrementIconScale = () => setIconScale((prev) => Math.max(5, prev - 5));

  // --- MAKE PUBLIC HANDLERS ---
  const handleMakePublicChange = (e) => {
    const val = e.target.value;
    if (val === '' || /^\d+$/.test(val)) {
      setMakePublicDays(val);
    }
  };
  const incrementMakePublic = () => {
    setMakePublicDays((prev) => {
      const num = prev === '' ? 0 : parseInt(prev, 10);
      return String(num + 1);
    });
  };
  const decrementMakePublic = () => {
    setMakePublicDays((prev) => {
      const num = prev === '' ? 0 : parseInt(prev, 10);
      return String(Math.max(0, num - 1));
    });
  };

  // --- NAVIGATION HANDLERS ---
  const handleNavFirst = () => setViewMode('PAGE1');
  const handleNavBack = () => {
    if (viewMode === 'PAGE5') setViewMode('PAGE4');
    else if (viewMode === 'PAGE4') setViewMode('PAGE3');
    else if (viewMode === 'PAGE3') setViewMode('PAGE2');
    else if (viewMode === 'PAGE2') setViewMode('PAGE1');
  };
  const handleNavForward = () => {
    if (viewMode === 'PAGE1') setViewMode('PAGE2');
    else if (viewMode === 'PAGE2') setViewMode('PAGE3');
    else if (viewMode === 'PAGE3') setViewMode('PAGE4');
    else if (viewMode === 'PAGE4') setViewMode('PAGE5');
  };
  const handleNavLast = () => setViewMode('PAGE5');

  // --- DIALOG HANDLERS ---
  const handleConfirmClose = () => executeClose();
  const handleCancelClose = () => setShowCloseAlert(false);
  const handleErrorDialogClose = () => setShowErrorDialog(false);

  // --- DERIVED STATE ---
  const canSave = isFormDirty;
  const canClose = true;

  // --- PORTAL OVERLAY (LOADING WHEEL) ---
  const portalOverlay = createPortal(
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        bgcolor: 'rgba(255,255,255,0.3)',
        zIndex: 100002,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'wait',
      }}>
      <CircularProgress sx={{ color: '#00B0FF' }} size={60} thickness={4} />
    </Box>,
    document.body,
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* SHOW LOADING OVERLAY */}
      {isProcessing && portalOverlay}

      {/* HEADER CONTROLS */}
      <Box
        sx={{
          height: '53px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f4f4f4',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          zIndex: 50,
          paddingLeft: '5px',
          paddingRight: '5px',
          mb: 0.25,
        }}>
        <Box sx={toolBlockStyle}>
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled={true}>
            <MenuAddNewGrey sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => canSave && handleHeaderAction('SAVE')}
            disabled={!canSave || isProcessing}>
            {canSave && !isProcessing ? (
              <MenuSave sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuSaveGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled={true}>
            <MenuDeleteGrey sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>
        <Box sx={toolBlockStyle}>
          {/* CLOSE (X) BUTTON - Handles the "Cancel/Discard" Logic */}
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => canClose && handleHeaderAction('CLOSE')}
            disabled={!canClose || isProcessing}>
            {canClose ? (
              <MenuIconCancel sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuIconCancelGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>
      </Box>

      {/* MAIN CONTENT AREA */}
      <Box
        sx={{
          px: '5px',
          mt: 0.25,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          flexGrow: 1,
          overflowY: 'auto',
        }}>
        {/* PAGE 1. MAIN LOGO SECTION */}
        <Box sx={{ display: viewMode === 'PAGE1' ? 'block' : 'none', mx: '30px' }}>
          <SettingsMainLogo
            logoUrl={logoUrl}
            logoScale={logoScale}
            handleImportClick={handleImportClick}
            fileInputRef={fileInputRef}
            handleFileChange={handleFileChange}
            handleEraserClick={handleEraserClick}
            handleScaleChange={handleScaleChange}
            incrementScale={incrementScale}
            decrementScale={decrementScale}
          />
        </Box>

        {/* PAGE 2. RESTRICTED SETTINGS SECTION */}
        <Box
          sx={{
            display: viewMode === 'PAGE2' ? 'block' : 'none',
            height: '100%',
            mx: '30px',
          }}>
          <SettingsRestricted
            ref={restrictedSettingsRef}
            onDirtyChange={setIsRestrictedDirty}
            rteFontType={rteFontType}
            rteFontSize={rteFontSize}
          />
        </Box>

        {/* PAGE 3. AI PROMPTS SECTION */}
        <Box sx={{ display: viewMode === 'PAGE3' ? 'block' : 'none', mx: '30px' }}>
          <SettingsAIPrompts
            descriptionPrompt={descriptionPrompt}
            setDescriptionPrompt={setDescriptionPrompt}
          />
        </Box>

        {/* PAGE 4. OTHER SETTINGS TABLE */}
        <Box sx={{ display: viewMode === 'PAGE4' ? 'block' : 'none', mx: '30px' }}>
          <SettingsOther
            iconScale={iconScale}
            handleIconScaleChange={handleIconScaleChange}
            incrementIconScale={incrementIconScale}
            decrementIconScale={decrementIconScale}
            websiteUrl={websiteUrl}
            handleTextChange={handleTextChange}
            emailFrom={emailFrom}
            setEmailFrom={setEmailFrom}
            makePublicDays={makePublicDays}
            handleMakePublicChange={handleMakePublicChange}
            incrementMakePublic={incrementMakePublic}
            decrementMakePublic={decrementMakePublic}
            rteFontType={rteFontType}
            setRteFontType={setRteFontType}
            rteFontSize={rteFontSize}
            setRteFontSize={setRteFontSize}
            imHeadingFontType={imHeadingFontType}
            setImHeadingFontType={setImHeadingFontType}
            imHeadingFontSize={imHeadingFontSize}
            setImHeadingFontSize={setImHeadingFontSize}
            allowRowBreaks={allowRowBreaks}
            setAllowRowBreaks={setAllowRowBreaks}
            useRoundedCorners={useRoundedCorners}
            setUseRoundedCorners={setUseRoundedCorners}
            roundedCornerRadius={roundedCornerRadius}
            setRoundedCornerRadius={setRoundedCornerRadius}
            makeUppercase={makeUppercase}
            setMakeUppercase={setMakeUppercase}
            broadcastDelay={broadcastDelay}
            setBroadcastDelay={setBroadcastDelay}
            isSecurityGroupLocked={isSecurityGroupLocked}
            setIsSecurityGroupLocked={setIsSecurityGroupLocked}
            formatDelayString={formatDelayString}
            openPasswordDialog={openPasswordDialog}
            deleteBouncesLock={deleteBouncesLock}
            setDeleteBouncesLock={setDeleteBouncesLock}
            importLimit={importLimit}
            setImportLimit={setImportLimit}
          />
        </Box>

        {/* PAGE 5. SOCIAL MEDIA LINKS TABLE */}
        <Box sx={{ display: viewMode === 'PAGE5' ? 'block' : 'none', mx: '30px' }}>
          <SettingsSocialMedia
            socialLinks={socialLinks}
            setSocialLinks={setSocialLinks}
          />
        </Box>
      </Box>

      {/* --- FOOTER (Pagination) --- */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: 3,
          py: 0.5,
          minHeight: '40px',
          borderTop: '1px solid black',
          backgroundImage: `linear-gradient(rgba(184, 231, 253, .85), rgba(184, 231, 253, .85)), url(${AdminBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          flexShrink: 0,
          gap: 1,
          overflow: 'visible',
          position: 'relative',
        }}>
        {/* PAGE INDICATOR DOTS */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            gap: 1,
          }}>
          {STEPS.map((step) =>
            viewMode === step ? (
              <CircleIcon key={step} sx={{ fontSize: '14px', color: '#0055ff' }} />
            ) : (
              <CircleOutlinedIcon key={step} sx={{ fontSize: '14px', color: '#666' }} />
            ),
          )}
        </Box>

        {/* NAVIGATION ARROWS */}
        <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
          <IconButton
            onClick={handleNavFirst}
            disabled={viewMode === 'PAGE1'}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <FirstPageIcon
              sx={{
                color: viewMode === 'PAGE1' ? 'grey' : 'black',
                fontSize: 22,
              }}
            />
          </IconButton>

          <IconButton
            onClick={handleNavBack}
            disabled={viewMode === 'PAGE1'}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <ArrowBackIosNewIcon
              sx={{
                color: viewMode === 'PAGE1' ? 'grey' : 'black',
                fontSize: 22,
              }}
            />
          </IconButton>

          <IconButton
            onClick={handleNavForward}
            disabled={viewMode === 'PAGE5'}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <ArrowForwardIosIcon
              sx={{
                color: viewMode === 'PAGE5' ? 'grey' : 'black',
                fontSize: 22,
              }}
            />
          </IconButton>

          <IconButton
            onClick={handleNavLast}
            disabled={viewMode === 'PAGE5'}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <LastPageIcon
              sx={{
                color: viewMode === 'PAGE5' ? 'grey' : 'black',
                fontSize: 22,
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* SECURITY PASSWORD DIALOG FORM */}
      <Dialog
        open={showPasswordDialog}
        onClose={handleClosePasswordDialog}
        PaperProps={{
          component: 'form',
          onSubmit: async (e) => {
            e.preventDefault();
            if (isChangingPassword) await handleChangePasswordSubmit();
            else await handleUnlockSubmit();
          },
          sx: { minWidth: '350px', borderRadius: 3, p: 1 },
        }}
        sx={{ zIndex: 100005 }}>
        <DialogTitle sx={{ pb: 1, pt: 2 }}>
          <Typography
            variant='h5'
            sx={{ color: '#000000', textAlign: 'center', fontWeight: 600 }}>
            {isChangingPassword ? 'Change Password' : 'Security Lock'}
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: '16px !important',
          }}>
          <TextField
            inputRef={passwordInputRef}
            label={isChangingPassword ? 'Current Password' : 'Password'}
            type={showCurrentPassword ? 'text' : 'password'}
            fullWidth
            variant='outlined'
            size='small'
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            error={!!dialogError && dialogError.toLowerCase().includes('current')}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge='end'
                    disabled={isDialogProcessing}>
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {isChangingPassword && (
            <>
              <TextField
                label='New Password'
                type={showNewPassword ? 'text' : 'password'}
                fullWidth
                variant='outlined'
                size='small'
                value={newPasswordInput}
                onChange={(e) => setNewPasswordInput(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge='end'
                        disabled={isDialogProcessing}>
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label='Confirm New Password'
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                variant='outlined'
                size='small'
                value={confirmPasswordInput}
                onChange={(e) => setConfirmPasswordInput(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge='end'
                        disabled={isDialogProcessing}>
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}
          {dialogError && (
            <Typography color='error' variant='body2'>
              {dialogError}
            </Typography>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}>
          {/* Top Row: Cancel and Update/Unlock Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Button
              type='button'
              variant='outlined'
              color='error'
              disabled={isDialogProcessing}
              onClick={handleClosePasswordDialog}
              sx={cancelButtonSx}>
              Cancel
            </Button>

            <Button
              type='submit'
              variant='contained'
              disabled={isDialogProcessing}
              sx={{
                textTransform: 'none',
                minWidth: 120,
                fontWeight: 500,
                bgcolor: '#1976d2',
                '&:hover': {
                  bgcolor: '#1565c0',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  transform: 'scale(1.03)',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#1976d2',
                  color: '#fff',
                },
              }}>
              {isDialogProcessing && (
                <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
              )}
              {isChangingPassword ? 'Update' : 'Unlock'}
            </Button>
          </Box>

          {/* Bottom Row: Change Password / Back Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {!isChangingPassword ? (
              <Button
                type='button'
                size='small'
                onClick={() => {
                  setDialogError('');
                  setIsChangingPassword(true);
                }}
                disableRipple
                sx={{
                  color: 'red',
                  fontWeight: 'normal',
                  textTransform: 'none',
                  textDecoration: 'underline',
                  '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' },
                }}>
                Change Password
              </Button>
            ) : (
              <Button
                type='button'
                size='small'
                onClick={() => {
                  setDialogError('');
                  setIsChangingPassword(false);
                }}
                disableRipple
                sx={{
                  color: 'gray',
                  fontWeight: 'normal',
                  textTransform: 'none',
                  textDecoration: 'underline',
                  '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' },
                }}>
                Back
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* DIALOGS */}
      <ConfirmDialog
        open={showCloseAlert}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        message='Do you want to discard those changes?'
        severity='warning'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
      />

      <ConfirmDialog
        open={showErrorDialog}
        onClose={handleErrorDialogClose}
        onConfirm={handleErrorDialogClose}
        message={errorMessage}
        severity={errorSeverity}
        confirmText='OK'
        sx={{ zIndex: 100005 }}
      />
    </Box>
  );
}
